/**
 * CDD Records API Service
 *
 * Transforms CaseReportResponse (WebSocket) data into CDDRecord shape for the portal UI.
 */

import {
  AssociatedPerson,
  type AssociatedPersonRole,
  AuditAction,
  AuditEntry,
  CDDCheck,
  CDDRecord,
  CDDStatus,
  CompanyCDDRecord,
  IndividualCDDRecord,
  RiskLevel,
  ScreeningMatchStatus,
  ScreeningResult,
  VerificationStatus
} from '../types/cdd-records.types';
import { normalizeCDDStatus } from '../utils/cddStatus';

import {
  CHECK_TYPE_MAP,
  SENIOR_MANAGING_OFFICIAL_ENTITY_TYPES,
  RISK_FACTOR_LABEL_MAP,
  ROLE_TYPE_MAP,
  kycOutcome,
  mapKycStatus,
} from './cddRecordsApiConfig';


const ARCAML_ENGINE_PROVIDERS = new Set(['SimpleKYC', 'IDVerse', 'simplekyc', 'idverse']);

function normalizeProvider(provider: string | undefined | null): string | undefined {
  if (!provider) return undefined;
  return ARCAML_ENGINE_PROVIDERS.has(provider) ? 'ARCaml Engine' : provider;
}

function normalizeRolePart(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

function roleDedupKey(role: any): string {
  return `${normalizeRolePart(role?.roleType)}:${normalizeRolePart(role?.roleDetail)}`;
}

function mergeUniqueRoles(existingRoles: any[] = [], incomingRoles: any[] = []): any[] {
  const seen = new Set(existingRoles.map(roleDedupKey));
  const uniqueIncoming = incomingRoles.filter((role) => {
    const key = roleDedupKey(role);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...existingRoles, ...uniqueIncoming];
}

/**
 * AL-519 item 4: a superseded principal role must not be shown as a current one.
 *
 * This was `isSystemAuthorisingPrincipal` and tested three conditions. Two are
 * now dead: after `f0d49dc` the server no longer synthesises a role carrying
 * `source: GATE_C2_USER_AUTHORISED` or `roleDetail: "AUTHORISING PRINCIPAL"`, so
 * nothing can match them. Leaving them in would tell the next reader the portal
 * still has to defend against a fabricated role that no longer exists.
 *
 * The third condition does real work and is all that remains — hence the rename.
 * This was never really about authorising principals; it is about not reporting
 * stale rows as current offices.
 *
 * Returning null REMOVES the role from the tag list, which is the point: a person
 * whose directorship was superseded is not a current director, and the CDD report
 * is the artefact the customer receives.
 */
function isSupersededPrincipalRole(role: any): boolean {
  return (
    normalizeRolePart(role?.roleType) === 'PRINCIPAL' &&
    normalizeRolePart(role?.status) === 'SUPERSEDED'
  );
}

/**
 * Exported for AL-519 item 6.
 *
 * `CDDRecordDetail` renders `profile.persons` — the RAW payload — rather than the
 * mapped record, so it has raw role objects and no mapped ids. It needs the same
 * vocabulary as every other renderer, and reusing this is the only way to get it
 * without a second copy of the ASIC, superseded, vehicle and SMO special cases
 * that would then drift.
 */
export function mapAssociatedPersonRole(role: any, entityType: string): AssociatedPersonRole | null {
  const roleType = normalizeRolePart(role?.roleType);
  const roleDetail = normalizeRolePart(role?.roleDetail);

  if (roleType === 'ASIC' && roleDetail === 'SHAREHOLDER') return 'beneficial-owner';
  if (roleType === 'ASIC' && roleDetail === 'DIRECTOR') return 'director';
  /**
   * AL-529: the registry's own office, not a guess.
   *
   * The server puts EVERY registry role under one type — `roleType: "ASIC"` with
   * the office in `roleDetail` — so `SECRETARY` was already in ROLE_TYPE_MAP and
   * `roleLabels`, wired end to end, and completely unreachable. The lookup at the
   * bottom of this function keys on `roleType`, which is never `SECRETARY`.
   *
   * On the reported case (N and K) both people held `Director, Shareholder` and
   * one also held `Secretary`. The Shareholder tag the one carried WAS his
   * secretaryship, relabelled by the fallback below. 27 such rows in test.
   */
  if (roleType === 'ASIC' && roleDetail === 'SECRETARY') return 'secretary';
  if (isSupersededPrincipalRole(role)) return null;
  if (roleType === 'PRINCIPAL' && entityType === 'vehicle') return 'trustee';
  /**
   * AL-519 item 7: the same UBO role, named for the entity type.
   *
   * Deliberately the same shape as the `PRINCIPAL` + `vehicle` case above, and
   * driven by one named set rather than an inline entity-type test, so compliance
   * can amend the membership in a single place. See
   * SENIOR_MANAGING_OFFICIAL_ENTITY_TYPES for why that set is as narrow as it is.
   */
  if (
    (roleType === 'UBO' || roleType === 'BENEFICIAL_OWNER') &&
    SENIOR_MANAGING_OFFICIAL_ENTITY_TYPES.has(entityType)
  ) {
    return 'senior-managing-official';
  }
  return (ROLE_TYPE_MAP[roleType] as AssociatedPersonRole | undefined) ?? 'other';
}

/**
 * AL-519 items 2 and 3 — the authority facts, lifted out while the raw roles are
 * still in scope.
 *
 * Both helpers read the RAW role objects, not the mapped `roles` array and not
 * the deduplicated list. Two reasons: the mapped array is strings, so the flags
 * are gone from it; and the dedup and superseded filtering can drop the very role
 * that carries the flag.
 */

/** They hold the office themselves — authority in their own right. */
function holdsOwnOffice(rawRoles: any[]): boolean {
  return rawRoles.some(
    (r) => normalizeRolePart(r?.roleType) === 'PRINCIPAL' && r?.isC2User === true,
  );
}

/**
 * The external principal who personally attested that the C2 User may act.
 *
 * `isAuthorising` is the raw column, so currency comes from pairing it with
 * `status`. Without the ACTIVE check a withdrawn attestation would still read as
 * a live basis for someone else's authority.
 */
function isAuthorisingPrincipalRole(rawRoles: any[]): boolean {
  return rawRoles.some(
    (r) =>
      normalizeRolePart(r?.roleType) === 'PRINCIPAL' &&
      r?.isAuthorising === true &&
      r?.isC2User === false &&
      normalizeRolePart(r?.status) === 'ACTIVE',
  );
}

function directorEvidenceLabel(role: any): string {
  const roleType = normalizeRolePart(role?.roleType);
  const source = normalizeRolePart(role?.source);
  const validatedAgainst = normalizeRolePart(
    role?.validatedAgainst ?? role?.validated_against,
  );

  if (roleType === 'ASIC' || source.includes('ASIC') || source.includes('SIMPLEKYC')) {
    return 'ASIC confirmed';
  }
  if (validatedAgainst === 'OPS_MANUAL' || source.includes('OPS')) {
    return 'OPS attested';
  }
  return 'Recorded';
}

/**
 * AL-523: projects the shared classification in KYC_STATUS_OUTCOME onto the document-status vocabulary.
 *
 * This used to enumerate statuses itself and ended in a bare `return 'pending'`, which is how OPS_VERIFIED
 * — a status the server emits only where an audited COMPLETE_OVERRIDE exists — came to render as Pending
 * for ten people. It also meant every FAILED* variant read as merely outstanding rather than rejected.
 *
 * Document status has no 'not-started', so a check that has not begun shows as pending here. That is a
 * vocabulary limit, not a judgement.
 */
function mapKycDocStatus(kycStatus: string): 'verified' | 'pending' | 'rejected' | 'expired' {
  const outcome = kycOutcome(kycStatus);
  if (outcome === 'verified') return 'verified';
  if (outcome === 'failed') return 'rejected';
  return 'pending';
}

function mapDocumentType(raw: string | undefined): 'passport' | 'drivers-licence' | 'medicare' | 'birth-certificate' | 'citizenship' | 'other' {
  const t = (raw ?? '').toLowerCase();
  if (t.includes('passport')) return 'passport';
  if (t.includes('driver') || t.includes('licence') || t.includes('license')) return 'drivers-licence';
  if (t.includes('medicare')) return 'medicare';
  if (t.includes('birth')) return 'birth-certificate';
  if (t.includes('citizen')) return 'citizenship';
  return 'other';
}

function mapPersonToAssociatedPerson(p: any, caseCompleted = false, index = 0, entityType = ''): AssociatedPerson {
  const kyc = p.identityCheck?.kycStatus ?? '';
  const sanctions = p.sanctionsScreening?.status ?? '';
  // AL-515: this used to upgrade a pending person to 'verified' whenever the case was complete,
  // on the assumption that a closed case implied OPS had approved everyone in it. It does not. A
  // case reaching COMPLETE:NOMORESTAGES means the workflow finished, not that each person was
  // checked — so anyone left pending was presented to the C1 as verified, including people OPS had
  // terminated.
  //
  // AG-351 removed exactly this inference server-side, where the reasoning is recorded on
  // assembleIdentityCheck: an audited COMPLETE_OVERRIDE is required as evidence before reporting
  // OPS_VERIFIED. That cleanup did not reach the client, so the fabrication survived here.
  //
  // Render what the server reports.
  //
  // AL-523: the sentence that stood here claimed OPS_VERIFIED "maps to 'verified' without help". It did
  // not — no mapper knew the word, so removing the inference above left ten OPS-verified people reading as
  // Pending. The mapping is now explicit in KYC_STATUS_OUTCOME and pinned by kycStatusVocabulary.test.ts.
  const personVerification = mapKycStatus(kyc);
  const personRisk: RiskLevel = sanctions === 'NEEDS_REVIEW' ? 'high' : 'low';

  const rawRoleObjects: any[] = p.roles ?? [];

  /**
   * AL-519 item 1: the C2_USER role is kept, not dropped.
   *
   * This used to suppress C2_USER whenever the person held any other role. That
   * made sense while C2_USER and APPROVED_USER both mapped to the single
   * 'authorised-representative' id — keeping it produced a duplicate-looking tag
   * that said nothing extra.
   *
   * It does not make sense now. C2_USER maps to its own id, and "completed the
   * onboarding form for this entity" is a distinct fact from holding an office.
   * Worse, the suppression removed the role for exactly the population this
   * ticket is about: a director who also filled in the form — Joanne Herron's
   * case — had the fact silently deleted precisely because she held another role.
   *
   * Consequence to expect: a person who is both will now show two tags where they
   * showed one. That is the intent, not a regression.
   */
  const mappedRoles = rawRoleObjects.map((role: any) => ({
    raw: role,
    mapped: mapAssociatedPersonRole(role, entityType),
  }));
  const roles = Array.from(
    new Set(
      mappedRoles
        .map(({ mapped }) => mapped)
        .filter((role): role is AssociatedPersonRole => role !== null),
    ),
  );
  const directorEvidence = Array.from(
    new Set(
      mappedRoles
        .filter(({ mapped }) => mapped === 'director')
        .map(({ raw }) => directorEvidenceLabel(raw)),
    ),
  );

  // AL-521: the server no longer sends totalOwnershipPercentage. It added the ASIC Shareholder figure to
  // the beneficial-ownership declaration — two statements about the same person, usually the same holding
  // twice — and reported Patrick Cho's 50% as 100%. It now sends each source separately.
  //
  // ownershipPercentage is derived here only where a single number is honest: one source, or two that
  // agree. Where they DIFFER it is left undefined on purpose, so the components that render one figure
  // show nothing rather than silently picking a side. The disagreement itself is the finding, and
  // AssociatedPersons renders it from `ownership`.
  const rawOwnership = p.ownership ?? null;
  const registered: number | null = rawOwnership?.registeredPercentage ?? null;
  const declared: number | null = rawOwnership?.declaredPercentage ?? null;
  const ownership = rawOwnership
    ? {
        registeredPercentage: registered,
        declaredPercentage: declared,
        sourcesAgree: rawOwnership.sourcesAgree === true,
      }
    : undefined;
  const ownershipPercentage =
    registered !== null && declared !== null
      ? (registered === declared ? registered : undefined)
      : (registered ?? declared ?? undefined);

  // Build identityDocuments from identityCheck + any raw document array on the person
  const identityDocuments: any[] = [];

  if (p.identityCheck?.kycStatus) {
    identityDocuments.push({
      id: `doc-${p.personId ?? p.id}-kyc`,
      type: mapDocumentType(p.identityCheck.documentType ?? p.identityCheck.docType ?? p.identityCheck.idType),
      documentNumber: p.identityCheck.documentNumber ?? p.identityCheck.idNumber ?? p.identityCheck.referenceNumber ?? '',
      issuingCountry: p.identityCheck.issuingCountry ?? undefined,
      issuingState: p.identityCheck.issuingState ?? undefined,
      issueDate: p.identityCheck.issueDate ?? undefined,
      expiryDate: p.identityCheck.expiryDate ?? undefined,
      status: mapKycDocStatus(kyc),
      verifiedDate: p.identityCheck.checkedAt ?? undefined,
      verifiedBy: normalizeProvider(p.identityCheck.provider) ?? undefined,
    });
  }

  // Also map any explicit documents array from the WS response
  (p.documents ?? []).forEach((doc: any, i: number) => {
    identityDocuments.push({
      id: doc.id ?? `doc-${p.personId ?? p.id}-${i}`,
      type: mapDocumentType(doc.documentType ?? doc.type),
      documentNumber: doc.documentNumber ?? doc.idNumber ?? '',
      issuingCountry: doc.issuingCountry ?? undefined,
      issuingState: doc.issuingState ?? undefined,
      issueDate: doc.issueDate ?? undefined,
      expiryDate: doc.expiryDate ?? undefined,
      status: doc.status ?? 'pending',
      verifiedDate: doc.verifiedAt ?? doc.verifiedDate ?? undefined,
      verifiedBy: normalizeProvider(doc.verifiedBy) ?? undefined,
      documentUrl: doc.documentUrl ?? doc.url ?? undefined,
    });
  });

  return {
    id: p.personId ?? p.id ?? `person-${index}`,
    firstName: p.firstName ?? '',
    lastName: p.lastName ?? '',
    dateOfBirth: p.dateOfBirth ?? undefined,
    contactDetails: {
      email: p.email ?? undefined,
      phone: p.phone ?? undefined,
    },
    verificationStatus: personVerification,
    riskLevel: personRisk,
    roles,
    roleEvidence: directorEvidence.length > 0
      ? { director: directorEvidence }
      : undefined,
    /**
     * AL-519 items 2 and 3. Read from the raw roles here because they do not
     * survive into `roles` above.
     *
     * `authorisedToAct` is strict-equality checked: the server writes it only on
     * the C2 User and never sends `false`, so absence means "not authorised" and
     * must not be coerced. `authorityBasis` is NOT set here — it needs to know
     * about the other people on the case, so it is filled in at record level.
     */
    authorisedToAct: p.authorisedToAct === true ? true : undefined,
    holdsOwnOffice: holdsOwnOffice(rawRoleObjects) ? true : undefined,
    isAuthorisingPrincipal: isAuthorisingPrincipalRole(rawRoleObjects) ? true : undefined,
    ownershipPercentage,
    ownership,
    isSanctioned: sanctions === 'NEEDS_REVIEW',
    identityDocuments: identityDocuments.length > 0 ? identityDocuments : undefined,
  };
}

/**
 * Transform a CaseReportResponse profile (from WebSocket) into a CDDRecord.
 */
export function transformC2ProfileToCDDRecord(profile: any): CDDRecord | null {
  if (!profile) return null;

  const person0 = profile.persons?.[0];
  const entityType = (profile.entityType || 'individual').toLowerCase();

  // Terminal report/stage outcomes must override stale processing values.
  const status: CDDStatus = normalizeCDDStatus(
    profile.status,
    profile.reportStatus,
    profile.stage?.status ?? profile.stage,
    profile.currentStage?.status ?? profile.currentStage,
    profile.currentOnboardingStage?.status ?? profile.currentOnboardingStage,
    profile.outcome,
  );

  // Overall verification: if case is complete, treat as verified regardless of individual person statuses
  // Merge dedup-linked persons into their linked counterpart (combine roles)
  const rawPersons: any[] = profile.persons ?? [];
  const dedupLinked = rawPersons.filter((p: any) => p.identityCheck?.kycStatus === 'DEDUP_LINKED');
  const nonDedup = rawPersons.filter((p: any) => p.identityCheck?.kycStatus !== 'DEDUP_LINKED');

  // For each dedup-linked person, find a matching non-dedup person and merge roles + documents
  // DEDUP_LINKED means the system has already determined these are the same person,
  // so we use flexible matching: same last name AND first name starts with same characters
  // Track which dedup-linked persons were successfully merged
  const unmatchedDedupLinked: any[] = [];

  for (const dp of dedupLinked) {
    const dpFirstName = (dp.firstName || '').toLowerCase().trim();
    const dpLastName = (dp.lastName || '').toLowerCase().trim();

    // Find a match: same last name AND (first names overlap — handles nicknames like ASH/ASHLEY)
    const match = nonDedup.find((p: any) => {
      const pFirstName = (p.firstName || '').toLowerCase().trim();
      const pLastName = (p.lastName || '').toLowerCase().trim();

      if (pLastName !== dpLastName) return false;

      // First names match exactly, one starts with the other, or one contains the other
      return pFirstName === dpFirstName ||
        pFirstName.startsWith(dpFirstName) ||
        dpFirstName.startsWith(pFirstName) ||
        pFirstName.includes(dpFirstName) ||
        dpFirstName.includes(pFirstName);
    });

    if (match) {
      // Preserve distinct roles held by the same person. Role detail is part of
      // the identity because PRINCIPAL/DIRECTOR and PRINCIPAL/Authorising
      // Principal have different meanings and only the latter is filtered.
      match.roles = mergeUniqueRoles(match.roles, dp.roles);

      // Merge documents from the dedup-linked person (don't duplicate)
      const existingDocs = match.documents ?? [];
      const newDocs = (dp.documents ?? []).filter((d: any) =>
        !existingDocs.some((ed: any) => ed.documentType === d.documentType && ed.id === d.id)
      );
      match.documents = [...existingDocs, ...newDocs];

      // Merge missing fields from the dedup-linked person (fill nulls)
      if (!match.dateOfBirth && dp.dateOfBirth) match.dateOfBirth = dp.dateOfBirth;
      if (!match.email && dp.email) match.email = dp.email;
      if (!match.phone && dp.phone) match.phone = dp.phone;
      if (!match.occupation && dp.occupation) match.occupation = dp.occupation;
      if (!match.identityCheck && dp.identityCheck) match.identityCheck = dp.identityCheck;
      if (!match.sanctionsScreening && dp.sanctionsScreening) match.sanctionsScreening = dp.sanctionsScreening;
    } else {
      // No match found - keep the dedup-linked person (they're still important)
      unmatchedDedupLinked.push(dp);
    }
  }

  // Include both non-dedup persons AND unmatched dedup-linked persons
  let allPersons: any[] = [...nonDedup, ...unmatchedDedupLinked];

  // Second pass: deduplicate by name similarity across ALL remaining persons
  // (handles cases where two entries for the same person exist without DEDUP_LINKED status)
  const deduped: any[] = [];
  for (const person of allPersons) {
    const pFirst = (person.firstName || '').toLowerCase().trim();
    const pLast = (person.lastName || '').toLowerCase().trim();

    const existingMatch = deduped.find((d) => {
      const dFirst = (d.firstName || '').toLowerCase().trim();
      const dLast = (d.lastName || '').toLowerCase().trim();
      if (dLast !== pLast) return false;
      return dFirst === pFirst ||
        dFirst.startsWith(pFirst) || pFirst.startsWith(dFirst) ||
        dFirst.includes(pFirst) || pFirst.includes(dFirst);
    });

    if (existingMatch) {
      // Merge roles by normalized type + detail, not type alone.
      existingMatch.roles = mergeUniqueRoles(existingMatch.roles, person.roles);
      // Merge documents
      const existingDocs = existingMatch.documents ?? [];
      const newDocs = (person.documents ?? []).filter((d: any) =>
        !existingDocs.some((ed: any) => ed.documentType === d.documentType && ed.id === d.id)
      );
      existingMatch.documents = [...existingDocs, ...newDocs];
      // Merge missing fields
      if (!existingMatch.dateOfBirth && person.dateOfBirth) existingMatch.dateOfBirth = person.dateOfBirth;
      if (!existingMatch.email && person.email) existingMatch.email = person.email;
      if (!existingMatch.phone && person.phone) existingMatch.phone = person.phone;
      if (!existingMatch.occupation && person.occupation) existingMatch.occupation = person.occupation;
      if (!existingMatch.identityCheck && person.identityCheck) existingMatch.identityCheck = person.identityCheck;
      if (!existingMatch.sanctionsScreening && person.sanctionsScreening) existingMatch.sanctionsScreening = person.sanctionsScreening;
      // Keep the longer first name (ASHLEY JOHN > ASH)
      if ((person.firstName || '').length > (existingMatch.firstName || '').length) {
        existingMatch.firstName = person.firstName;
      }
    } else {
      deduped.push(person);
    }
  }
  allPersons = deduped;

  let verificationStatus: VerificationStatus;
  if (status === 'completed' || status === 'active') {
    verificationStatus = 'verified';
  } else {
    const allVerified = allPersons.length > 0 && allPersons.every(
      (p) => mapKycStatus(p.identityCheck?.kycStatus ?? '') === 'verified'
    );
    const anyFailed = allPersons.some(
      (p) => mapKycStatus(p.identityCheck?.kycStatus ?? '') === 'failed'
    );
    verificationStatus = allVerified ? 'verified' : anyFailed ? 'failed' : 'pending';
  }

  // Risk from riskAssessment or any person with NEEDS_REVIEW sanctions
  const overallRating = profile.riskAssessment?.overallRating ?? '';
  const anySanctionsReview = allPersons.some((p) => p.sanctionsScreening?.status === 'NEEDS_REVIEW');
  const riskLevel: RiskLevel =
    overallRating === 'high' || anySanctionsReview ? 'high' :
      overallRating === 'medium' ? 'medium' : 'low';

  // Build checksLog from all checks
  const checks: CDDCheck[] = (profile.checksLog ?? []).map((c: any, i: number) => {
    // Status may come as c.status (mapped) or c.result (raw), or embedded in c.detail ("NAME — STATUS")
    const rawStatus: string = (c.status ?? c.result ?? c.kybStatus ?? '').toUpperCase();
    const detailStatus = typeof c.detail === 'string'
      ? (c.detail.split('—').pop()?.trim() ?? '').toUpperCase()
      : '';
    const effectiveStatus = rawStatus || detailStatus;
    const result: CDDCheck['result'] =
      effectiveStatus === 'PASS' || effectiveStatus === 'PASSEDCLEAN' ? 'pass' :
        effectiveStatus === 'FAIL' || effectiveStatus === 'FAILED' ? 'fail' :
          effectiveStatus === 'NEEDS_REVIEW' || effectiveStatus === 'NEEDS_OPS_REVIEW' ? 'review-required' :
            effectiveStatus === 'INCONCLUSIVE' ? 'inconclusive' :
              'pending';
    return {
      id: `check-${i}`,
      checkType: CHECK_TYPE_MAP[c.checkType] ?? 'other',
      checkDate: c.checkedAt ?? profile.createdDate ?? new Date().toISOString(),
      performedBy: normalizeProvider(c.provider) ?? 'System',
      status: 'completed' as const,
      result,
      details: c.detail,
      // AL-482: the name the check was performed on. Survives the server's C1 strip, unlike detail.
      subject: c.subject,
      dataSource: normalizeProvider(c.provider),
      scope: c.scope,
    };
  });

  // Build screening results from all persons' sanctionsScreening
  const screeningResults: ScreeningResult[] = allPersons
    .filter((p) => p.sanctionsScreening?.checkedAt)
    .map((p, i) => ({
      id: `screen-${i}`,
      screeningType: 'PEP' as const,
      screeningDate: p.sanctionsScreening.checkedAt,
      screenedEntity: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
      provider: normalizeProvider(p.sanctionsScreening.provider) ?? 'ARCaml Engine',
      status: (p.sanctionsScreening.status === 'CLEAN' || p.sanctionsScreening.status === 'CLEARED' ? 'clear' : 'potential-match') as ScreeningMatchStatus,
    }));

  // Build audit trail from stageHistory + flagHistory + opsActions
  const stageEntries: AuditEntry[] = (profile.stageHistory ?? []).map((s: any, i: number) => ({
    id: `stage-${i}`,
    timestamp: s.happenedAt ?? profile.createdDate,
    userId: 'system',
    userName: 'System',
    action: 'update' as AuditAction,
    entityType: 'CDD',
    entityId: profile.id,
    notes: s.scope !== 'parent' ? `[${s.scope}] ${s.stage} → ${s.status}` : `${s.stage} → ${s.status}`,
  }));

  const flagEntries: AuditEntry[] = allPersons.flatMap((p: any, pi: number) =>
    (p.flagHistory ?? []).map((f: any, fi: number) => ({
      id: `flag-${pi}-${fi}`,
      timestamp: f.actionAt ?? profile.createdDate,
      userId: f.setBy === 'SYSTEM' || f.setBy === 'HEARTBEAT' ? 'system' : f.setBy,
      userName: f.setBy === 'SYSTEM' ? 'System' : f.setBy === 'HEARTBEAT' ? 'Heartbeat' : f.setBy,
      action: f.action === 'SET' ? 'update' : f.action === 'CLEAR' ? 'verify' : 'review' as AuditAction,
      entityType: 'CDD',
      entityId: profile.id,
      notes: `[FLAG ${f.action}] ${f.flag}${f.comments ? `: ${f.comments}` : ''}`,
    }))
  );

  const opsEntries: AuditEntry[] = (profile.opsActions ?? []).map((o: any, i: number) => ({
    id: `ops-${i}`,
    timestamp: o.actionAt ?? profile.createdDate,
    userId: o.opsUser ?? 'system',
    userName: o.opsUser ?? 'Ops',
    action: 'approve' as AuditAction,
    entityType: 'CDD',
    entityId: profile.id,
    notes: `[OPS] ${o.actionType}${o.comments ? `: ${o.comments}` : ''}`,
  }));

  const allAuditEntries: AuditEntry[] = [...stageEntries, ...flagEntries, ...opsEntries]
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const isCaseCompleted = status === 'completed' || status === 'active';

  // Map all persons to AssociatedPerson[]
  const mappedPersons: AssociatedPerson[] = allPersons.map((p, idx) => mapPersonToAssociatedPerson(p, isCaseCompleted, idx, entityType));

  /**
   * AL-519 item 3 — resolve how each authorised person's authority is established.
   *
   * Done here rather than in `mapPersonToAssociatedPerson` because the delegated
   * case needs to know about the OTHER people on the case: a person is only
   * "authorised by X" if X is actually present and is an active authorising
   * principal. A per-person mapper cannot see that.
   *
   * ⚠️ The order of these branches is the whole safety property, and it is NOT a
   * style choice. `'unknown'` is returned when authority is confirmed but no
   * basis can be established, and it must never collapse into `'delegated'`.
   *
   * The authorisation gate can be set by ASIC matching the person as a director
   * just as readily as by somebody attesting for them. So the absence of a
   * self-claim row is MISSING INFORMATION, not evidence that someone delegated.
   * AL-445 is exactly this mistake: it produced a CDD report telling a customer
   * he was authorised to act by an external principal when there was no such
   * principal and he was a director in his own right.
   *
   * The platform assembler refuses this and raises an alert instead. The C1 path
   * has no such alert, so this is the only place the distinction is enforced for
   * the C1 report.
   */
  const authorisingPrincipal = mappedPersons.find((person) => person.isAuthorisingPrincipal === true);
  const authorisingPrincipalName = authorisingPrincipal
    ? `${authorisingPrincipal.firstName} ${authorisingPrincipal.lastName}`.trim()
    : undefined;

  const associatedPersons: AssociatedPerson[] = mappedPersons.map((person) => {
    if (person.authorisedToAct !== true) return person;
    if (person.holdsOwnOffice === true) {
      return { ...person, authorityBasis: 'own-right' as const };
    }
    if (authorisingPrincipalName) {
      return {
        ...person,
        authorityBasis: 'delegated' as const,
        authorisedByName: authorisingPrincipalName,
      };
    }
    // Authority confirmed, basis not established. Render the fact, never a basis.
    return { ...person, authorityBasis: 'unknown' as const };
  });

  const base = {
    id: profile.id,
    referenceNumber: profile.referenceNumber ?? `CDD-${profile.id}`,
    entityType: entityType as any,
    status,
    riskLevel,
    verificationStatus,
    createdDate: profile.createdDate ?? new Date().toISOString(),
    createdBy: 'C2',
    lastModifiedDate: profile.lastModifiedDate,
    associatedPersons,
    screeningResults,
    checksLog: {
      checks,
      complianceStatus: verificationStatus === 'verified' ? 'compliant' : 'review-required' as any,
    },
    auditTrail: {
      entries: allAuditEntries,
      createdDate: profile.createdDate ?? new Date().toISOString(),
      createdBy: 'System',
    },
    customerAssessment: profile.riskAssessment ? {
      riskAssessments: [{
        id: `ra-${profile.id}`,
        // AL-437: absent stays absent. Do not reintroduce fallbacks here — the report
        // nulls assessedAt and omits assessedBy, so any default is invented data in a
        // compliance document. overallRating IS supplied (derived from enhanced_profile
        // in CddReportAssembler), so it needs no fallback either.
        assessmentDate: profile.riskAssessment.assessedAt ?? undefined,
        assessedBy: profile.riskAssessment.assessedBy ?? undefined,
        overallRiskRating: profile.riskAssessment.overallRating as RiskLevel,
        customerRiskFactors: Object.entries(profile.riskAssessment.factors ?? {}).map(([factor, rating]) => {
          return {
            factor: RISK_FACTOR_LABEL_MAP[factor] ?? factor,
            rating: rating as RiskLevel,
          };
        }),
        // AL-437: mapped without a default, so they are undefined when the report sends
        // no factors block — which is always, at present. deliveryChannelRisk is gone
        // entirely; it was a literal with no data source.
        productServiceRisk: profile.riskAssessment.factors?.productRisk as RiskLevel | undefined,
        geographicRisk: profile.riskAssessment.factors?.countryRisk as RiskLevel | undefined,
        industryRisk: profile.riskAssessment.factors?.industryRisk as RiskLevel | undefined,
        enhancedDueDiligenceRequired: profile.riskAssessment.eddRequired ?? profile.enhancedDueDiligence ?? false,
        nextReviewDate: profile.riskAssessment.nextReviewDate ?? '',
      }],
      currentRiskRating: (profile.riskAssessment.overallRating ?? 'low') as RiskLevel,
    } : undefined,
    notes: profile.c1Identifier ? `c1Identifier: ${profile.c1Identifier}` : undefined,
    // AL-438: mapped here in `base` so it reaches every entity type in one line,
    // and independently of riskAssessment — customerAssessment below is only
    // built when the report carries a riskAssessment block, so a field placed
    // there would vanish for any case without one.
    //
    // Two source shapes are read because the CDD report assembler lives in
    // arcaml-c1-services, which is not in this workspace, so which level it
    // emits at could not be confirmed here. Top level is what AL-438 documents;
    // the entity fallback costs nothing and avoids a silent blank if it is
    // nested. Worth confirming against the assembler and reducing to one.
    //
    // No fallback string. Per AL-437 absent stays absent: inventing a
    // placeholder in a compliance document is worse than showing nothing.
    natureOfBusiness: profile.natureOfBusiness ?? profile.entity?.natureOfBusiness ?? undefined,
  };

  if (entityType === 'individual' || !entityType) {
    const primaryPerson = allPersons[0] ?? person0;
    return {
      ...base,
      entityType: 'individual',
      profile: {
        id: primaryPerson?.personId ?? profile.id,
        firstName: primaryPerson?.firstName ?? '',
        lastName: primaryPerson?.lastName ?? '',
        dateOfBirth: primaryPerson?.dateOfBirth ?? undefined,
        occupation: primaryPerson?.occupation ?? undefined,
        contactDetails: {
          email: primaryPerson?.email ?? undefined,
          phone: primaryPerson?.phone ?? undefined,
        },
        verificationStatus: mapKycStatus(primaryPerson?.identityCheck?.kycStatus ?? ''),
        riskLevel,
      },
    } as IndividualCDDRecord;
  }

  // Company entity type
  if (entityType === 'company') {
    const ent = profile.entity ?? {};
    return {
      ...base,
      entityType: 'company',
      profile: {
        id: profile.id,
        entityType: 'company',
        legalName: ent.legalName ?? '',
        tradingName: ent.tradingName ?? undefined,
        registration: {
          abn: ent.abn ?? undefined,
          acn: ent.acn ?? undefined,
          registrationDate: ent.dateOfIncorporation ?? undefined,
          companyType: ent.entityType ?? undefined,
          companyClass: ent.entityClass ?? undefined,
          companySubclass: ent.entitySubType ?? undefined,
        },
        registeredAddress: {
          line1: ent.registeredAddress?.line1 ?? '',
          line2: ent.registeredAddress?.line2 ?? undefined,
          suburb: ent.registeredAddress?.suburb ?? undefined,
          state: ent.registeredAddress?.state ?? undefined,
          postcode: ent.registeredAddress?.postcode ?? undefined,
          country: ent.registeredAddress?.country ?? undefined,
        },
        principalPlaceOfBusiness: ent.principalPlaceOfBusiness ? {
          line1: ent.principalPlaceOfBusiness.line1 ?? '',
          suburb: ent.principalPlaceOfBusiness.suburb ?? undefined,
          state: ent.principalPlaceOfBusiness.state ?? undefined,
          postcode: ent.principalPlaceOfBusiness.postcode ?? undefined,
          country: ent.principalPlaceOfBusiness.country ?? undefined,
        } : undefined,
        industryCode: ent.industryCode ?? undefined,
        industryDescription: ent.industryDescription ?? undefined,
        dateOfIncorporation: ent.dateOfIncorporation ?? undefined,
        // Determine ASIC status: if entity.status exists use it, otherwise derive from case status + KYB
        status: ent.status
          ? (['active', 'registered'].includes(ent.status.toLowerCase()) ? 'registered' : 'deregistered')
          : (status === 'completed' || status === 'active' || ent.kybStatus === 'PASSEDCLEAN')
            ? 'registered'
            : 'deregistered',
        asicStatus: ent.status ?? (ent.kybStatus === 'PASSEDCLEAN' ? 'Registered' : undefined),
      },
    } as CompanyCDDRecord;
  }

  // Vehicle entity type (trusts, partnerships) — use entity object for profile
  if (entityType === 'vehicle' && profile.entity) {
    const ent = profile.entity;

    // Map entity subtype abbreviations to TrustType enum values
    const trustTypeMap: Record<string, string> = {
      'DTT': 'discretionary', 'DIT': 'discretionary', 'DFT': 'discretionary', 'DST': 'discretionary',
      'HDT': 'hybrid', 'HYT': 'hybrid',
      'UNT': 'unit', 'FUT': 'fixed', 'PUT': 'unit', 'MUT': 'unit', 'LUT': 'unit',
      'FST': 'fixed',
      'SMF': 'superannuation', 'SMSF': 'superannuation', 'SFT': 'superannuation', 'APF': 'superannuation', 'SAF': 'superannuation',
      'TRT': 'discretionary',
      'TST': 'testamentary',
      'BRT': 'bare',
      'CST': 'charitable', 'CLT': 'charitable', 'CRT': 'charitable',
      'MIT': 'managed-investment', 'RIT': 'managed-investment',
      'PRT': 'other', 'LPT': 'other', 'CPT': 'other', 'RPT': 'other',
    };
    const rawSubType = (ent.entitySubType || ent.entityType || '').toUpperCase();
    const mappedTrustType = trustTypeMap[rawSubType] || 'other';

    return {
      ...base,
      entityType: 'trust',
      profile: {
        id: profile.id,
        entityType: 'trust',
        trustName: ent.legalName ?? '',
        trustType: mappedTrustType as any,
        registration: {
          abn: ent.abn ?? undefined,
          acn: ent.acn ?? undefined,
          trustDeedDate: ent.dateOfIncorporation ?? undefined,
        },
        trusteeType: 'individual' as const,
        businessAddress: ent.registeredAddress ? {
          line1: ent.registeredAddress.line1 ?? '',
          suburb: ent.registeredAddress.suburb ?? undefined,
          state: ent.registeredAddress.state ?? undefined,
          postcode: ent.registeredAddress.postcode ?? undefined,
          country: ent.registeredAddress.country ?? undefined,
        } : undefined,
        establishmentDate: ent.dateOfIncorporation ?? undefined,
        status: ent.status
          ? (['active', 'registered'].includes(ent.status.toLowerCase()) ? 'active' : 'terminated')
          : (status === 'completed' || status === 'active')
            ? 'active'
            : 'active',
      },
      roles: {
        trustees: associatedPersons.filter((p) =>
          p.roles.some((r) => r === 'trustee' || r === 'key-controller')
        ),
        /**
         * AL-519 item 7: deliberately NOT using isBeneficialOwnerRole here.
         *
         * This is the trust rollup, and `trust` is not in
         * SENIOR_MANAGING_OFFICIAL_ENTITY_TYPES, so the second id cannot occur on
         * this path. Matching it anyway would also be wrong in meaning — a senior
         * managing official is not a trust beneficiary.
         */
        beneficiaries: associatedPersons.filter((p) =>
          p.roles.some((r) => r === 'beneficial-owner' || r === 'beneficiary')
        ),
        settlors: associatedPersons.filter((p) =>
          p.roles.some((r) => r === 'settlor')
        ),
      },
    } as any;
  }

  // Fallback: individual-shaped record for unknown types
  return {
    ...base,
    entityType: 'individual',
    profile: {
      id: (allPersons[0] ?? person0)?.personId ?? profile.id,
      firstName: (allPersons[0] ?? person0)?.firstName ?? '',
      lastName: (allPersons[0] ?? person0)?.lastName ?? '',
      dateOfBirth: (allPersons[0] ?? person0)?.dateOfBirth ?? undefined,
      occupation: (allPersons[0] ?? person0)?.occupation ?? undefined,
      contactDetails: {
        email: (allPersons[0] ?? person0)?.email ?? undefined,
        phone: (allPersons[0] ?? person0)?.phone ?? undefined,
      },
      verificationStatus: mapKycStatus((allPersons[0] ?? person0)?.identityCheck?.kycStatus ?? ''),
      riskLevel,
    },
  } as IndividualCDDRecord;
}
